/**
 * Générateur — Attendu 956 — Connaître les propriétés des médiatrices et du
 * cercle circonscrit dans le cas de triangles particuliers.
 *
 * Niveau : 5ème | Thème #10 Triangles (O15) | Domaine : Espace et géométrie
 * Source prof : cluster 2, Ex.1-6 + Créé n°33/34, extraction exerciseur/triangles.
 *
 * ADAPTATION (décision explicite, protocole gen-cluster) : « tracer le cercle
 * circonscrit au compas » n'est pas scorable en geste libre. On garde UNE
 * construction réelle (médiatrice = droite = programme_construction) et on
 * remplace le tracé du cercle par le PLACEMENT du centre O sur un repère
 * (placer_points_2d) + des cartes de propriétés (QCM / V-F / calcul).
 *
 * 6 cartes :
 *  1. mediatrice — Ex.1 : trace la médiatrice d'un segment (programme_construction).
 *  2. centre     — Ex.2/3/5 : place le centre O du cercle circonscrit (repère).
 *                  Triangle = O + points ENTIERS du cercle de rayon 5 → le
 *                  circoncentre est EXACTEMENT O, sur une intersection.
 *  3. rectangle  — Ex.6 : l'hypoténuse est un DIAMÈTRE du cercle circonscrit (QCM).
 *  4. proprietes — Créé n°33 : vrai/faux médiatrices & cercle circonscrit.
 *  5. rayon      — Créé n°34 a-b : rayon = hypoténuse/2 (saisie à trous).
 *  6. diametre   — Créé n°34 c : quel segment est un diamètre ? (QCM).
 *
 * Solveurs : centre (circoncentre recalculé des sommets placés), rectangle,
 * rayon, diametre re-dérivés ; médiatrice (geste) et V-F (connaissances)
 * déclaratifs.
 */

import {
    randInt, randomChoice, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, makeProgrammeConstruction, basePayload,
} from '../../../utils';

const ATTENDU_ID = 956;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_MEDIATRICE = 'Tracer une médiatrice';
const L_CENTRE = 'Placer le centre du cercle circonscrit';
const L_RECTANGLE = 'Triangle rectangle : l\'hypoténuse';
const L_PROPRIETES = 'Vrai ou faux : médiatrices & cercle';
const L_RAYON = 'Rayon et hypoténuse';
const L_DIAMETRE = 'Quel segment est un diamètre ?';

const PX = 30;
const VBC = { w: 600, h: 400 };
const COL_B = '#2563EB';

const pickLetters = (n) => shuffle('ABCDEFGHJKLMNPRSTUVWXYZ'.split('')).slice(0, n);
const frCm = (v) => (Number.isInteger(v) ? String(v) : String(v).replace('.', ','));

// ═══════════════════════════════════════════════════════════
// Carte 1 — Médiatrice d'un segment (Ex.1)
// ═══════════════════════════════════════════════════════════

const genMediatricePC = () => {
    // Segment [EF] oblique ; médiatrice = droite par le milieu, perpendiculaire.
    const [nE, nF] = pickLetters(2);
    const cx = 300, cy = 200;
    const ang = randomChoice([0, 20, 35, 90, 110, 155]);
    const half = randInt(90, 120);
    const rad = (ang * Math.PI) / 180;
    const ux = Math.cos(rad), uy = Math.sin(rad);
    const E = { x: Math.round(cx - half * ux), y: Math.round(cy - half * uy) };
    const F = { x: Math.round(cx + half * ux), y: Math.round(cy + half * uy) };
    const M = { x: (E.x + F.x) / 2, y: (E.y + F.y) / 2 };
    // Perpendiculaire : direction (−uy, ux). Deux points de part et d'autre.
    const d = 110;
    const p1 = { x: Math.round(M.x - d * -uy), y: Math.round(M.y - d * ux) };
    const p2 = { x: Math.round(M.x + d * -uy), y: Math.round(M.y + d * ux) };
    const texte = `<p>Trace la <b>médiatrice</b> du segment [${nE}${nF}] `
        + '(la droite perpendiculaire à ce segment et passant par son milieu).</p>';
    const payload = makeProgrammeConstruction(
        {
            pxPerCm: PX,
            tolerance: 12,
            viewBox: VBC,
            points: [{ name: nE, x: E.x, y: E.y }, { name: nF, x: F.x, y: F.y }],
            preShapes: [{ type: 'line', from: nE, to: nF }],
            tasks: [{
                id: 'med',
                kind: 'line',
                consigne: `Trace la médiatrice de [${nE}${nF}] (place deux points de la droite, de part et d'autre du segment).`,
                color: COL_B,
                shape: { type: 'line', p1, p2 },
            }],
        },
        texte,
        opts(L_MEDIATRICE),
    );
    payload._meta.generatorKey = 'mediatrice_programme_construction';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — Placer le centre du cercle circonscrit (Ex.2/3/5)
// ═══════════════════════════════════════════════════════════

// Points ENTIERS du cercle de rayon 5 (triplets pythagoriciens 3-4-5).
const L5 = [
    [5, 0], [4, 3], [3, 4], [0, 5], [-3, 4], [-4, 3],
    [-5, 0], [-4, -3], [-3, -4], [0, -5], [3, -4], [4, -3],
];
const CENTRES_O = [[0, 0], [1, 0], [0, 1], [1, 1], [-1, 0], [0, -1], [-1, -1], [2, 1], [-1, 1], [1, -1]];
const ISO_TRIPLES = [
    [[0, 5], [-4, -3], [4, -3]],
    [[0, -5], [-4, 3], [4, 3]],
    [[5, 0], [-3, 4], [-3, -4]],
    [[-5, 0], [3, 4], [3, -4]],
];
const aire2 = (a, b, c) => Math.abs((b[0] - a[0]) * (c[1] - a[1]) - (c[0] - a[0]) * (b[1] - a[1]));

const genCentrePlacer = () => {
    const fam = randomChoice(['general', 'general', 'rectangle', 'isocele']);
    const [ox, oy] = randomChoice(CENTRES_O);
    let pts;
    if (fam === 'rectangle') {
        const p = randomChoice(L5);
        const anti = [-p[0], -p[1]];
        let q;
        do { q = randomChoice(L5); } while (
            (q[0] === p[0] && q[1] === p[1]) || (q[0] === anti[0] && q[1] === anti[1]));
        pts = shuffle([p, anti, q]);
    } else if (fam === 'isocele') {
        pts = randomChoice(ISO_TRIPLES);
    } else {
        for (let t = 0; t < 100; t++) {
            const s = shuffle(L5).slice(0, 3);
            if (aire2(s[0], s[1], s[2]) >= 30) { pts = s; break; }
        }
        if (!pts) pts = [[5, 0], [0, 5], [-5, 0]];
    }
    const [nA, nB, nC] = pickLetters(3);
    const noms = [nA, nB, nC];
    const dejaPlaces = pts.map((p, i) => ({ x: p[0] + ox, y: p[1] + oy, label: noms[i] }));
    const texte = `<p>Les trois sommets du triangle ${nA}${nB}${nC} sont placés dans le repère. `
        + 'Place le point <b>O</b>, centre du cercle circonscrit au triangle '
        + '(le point situé à égale distance des trois sommets).</p>';
    const payload = basePayload('association', texte, {
        subType: 'placer_points_2d',
        repere: { xMin: -7, yMin: -7, xMax: 7, yMax: 7, xStep: 1, yStep: 1 },
        pointsToPlace: ['O'],
        dejaPlaces,
        bonne_reponse: JSON.stringify({ O: `${ox};${oy}` }),
    }, opts(L_CENTRE));
    payload._meta.generatorKey = 'centre_placer_points_2d';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — Triangle rectangle : l'hypoténuse est un diamètre (Ex.6)
// ═══════════════════════════════════════════════════════════

const genRectangleQcm = () => {
    const [nH, nI, nJ] = pickLetters(3);   // rectangle en nH → hypoténuse [nI nJ]
    const texte = `<p>${nH}${nI}${nJ} est un triangle rectangle en ${nH}. On trace son cercle circonscrit. `
        + `Le segment [${nI}${nJ}] (l'hypoténuse) est un … de ce cercle.</p>`;
    const payload = makeQcm(['diamètre', 'rayon', 'corde', 'axe de symétrie'], 'diamètre', texte, {
        ...opts(L_RECTANGLE),
    });
    payload._meta.generatorKey = 'rectangle_qcm_unique';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — Vrai ou faux (Créé n°33)
// ═══════════════════════════════════════════════════════════

const VF_VRAIS = [
    'La médiatrice d\'un segment est l\'ensemble des points équidistants de ses extrémités.',
    'Les trois médiatrices d\'un triangle sont concourantes.',
    'Le centre du cercle circonscrit est équidistant des trois sommets du triangle.',
    'Dans un triangle rectangle, le centre du cercle circonscrit est le milieu de l\'hypoténuse.',
    'Dans un triangle isocèle, le centre du cercle circonscrit est situé sur l\'axe de symétrie.',
    'Le cercle circonscrit à un triangle passe par ses trois sommets.',
];
const VF_FAUX = [
    'Le centre du cercle circonscrit est toujours situé à l\'intérieur du triangle.',
    'Le centre du cercle circonscrit est le point d\'intersection des hauteurs du triangle.',
    'Dans tout triangle, le centre du cercle circonscrit est le milieu d\'un des côtés.',
];

const genProprietesVf = () => {
    const vrais = shuffle(VF_VRAIS).slice(0, 4).map((t) => ({ t, v: 0 }));
    const faux = shuffle(VF_FAUX).slice(0, 2).map((t) => ({ t, v: 1 }));
    const mixed = shuffle([...vrais, ...faux]);
    const texte = '<p>Pour chaque affirmation, indique si elle est vraie ou fausse.</p>';
    const payload = makeVraiFaux(mixed.map((x) => x.t), mixed.map((x) => x.v), texte, {
        ...opts(L_PROPRIETES),
    });
    payload._meta.generatorKey = 'proprietes_tableau_vf';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte 5 — Rayon et hypoténuse (Créé n°34 a-b)
// ═══════════════════════════════════════════════════════════

const genRayonTrous = () => {
    const [nA, nB, nC] = pickLetters(3);
    const bc = randomChoice([6, 8, 10, 12]);          // hypoténuse → rayon = bc/2
    const r = randomChoice([2.5, 3, 3.5, 4]);         // rayon → hypoténuse = 2r
    const phrase = `Le triangle ${nA}${nB}${nC} est rectangle en ${nA} et ${nB}${nC} = ${bc} cm : `
        + `le rayon de son cercle circonscrit vaut {{${frCm(bc / 2)}}} cm. `
        + `Un autre triangle rectangle est inscrit dans un cercle de rayon ${frCm(r)} cm : `
        + `son hypoténuse mesure {{${frCm(2 * r)}}} cm.`;
    const texte = '<p>Utilise la propriété « le rayon du cercle circonscrit à un triangle rectangle '
        + 'est la moitié de l\'hypoténuse ». Complète :</p>';
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_RAYON), mode: 'saisie', strict: false,
    });
    payload._meta.generatorKey = 'rayon_texte_trou_saisie';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte 6 — Quel segment est un diamètre ? (Créé n°34 c)
// ═══════════════════════════════════════════════════════════

const genDiametreQcm = () => {
    const [nD, nE, nF] = pickLetters(3);
    // rectangle en nE → hypoténuse (diamètre) = [nD nF] (les deux autres sommets).
    const correct = `[${nD}${nF}]`;
    const choix = [correct, `[${nD}${nE}]`, `[${nE}${nF}]`];
    const texte = `<p>${nD}${nE}${nF} est un triangle rectangle en ${nE}. `
        + 'Quel segment est un diamètre de son cercle circonscrit ?</p>';
    const payload = makeQcm(choix, correct, texte, { ...opts(L_DIAMETRE) });
    payload._meta.generatorKey = 'diametre_qcm_unique';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    mediatrice_programme_construction: genMediatricePC,
    centre_placer_points_2d: genCentrePlacer,
    rectangle_qcm_unique: genRectangleQcm,
    proprietes_tableau_vf: genProprietesVf,
    rayon_texte_trou_saisie: genRayonTrous,
    diametre_qcm_unique: genDiametreQcm,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', programme_construction: 'Construction',
    placer_points_2d: 'Repère', tableau_vf: 'Vrai/Faux', texte_trou_saisie: 'Saisie trous',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'mediatrice', label: L_MEDIATRICE, formatsList: buildFormats('mediatrice') },
    { id: 'centre', label: L_CENTRE, formatsList: buildFormats('centre') },
    { id: 'rectangle', label: L_RECTANGLE, formatsList: buildFormats('rectangle') },
    { id: 'proprietes', label: L_PROPRIETES, formatsList: buildFormats('proprietes') },
    { id: 'rayon', label: L_RAYON, formatsList: buildFormats('rayon') },
    { id: 'diametre', label: L_DIAMETRE, formatsList: buildFormats('diametre') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    genMediatricePC, genCentrePlacer, genRectangleQcm, genProprietesVf,
    genRayonTrous, genDiametreQcm,
};
